import * as yup from 'yup';
import { applicationStatus, ApplicationType } from './enums';
import {
  createApplication,
  cloneApplication,
  requestChangeActiveLine,
  alignLineAndCalculatePaymentTable,
  createPlanSnapshot,
} from '../components/API';
import { editLine } from '../components/API/API';
import { IBusiness } from '../Types/business';
import { ILine } from '../global';
import { IPreActivationStep } from '../Types';

export const initialValues = { acceptedTerms: false };

export function validation(setDisplayError: (...args) => any) {
  return yup.object({
    acceptedTerms: yup.bool().test('accepted-terms', 'null', (checked) => {
      if (checked) {
        setDisplayError(false);
      }
      return true;
    }),
  });
}

function generateSixDigitId() {
  const id = Math.random()
    .toString(36)
    .replace(/[^a-z0-9]+/g, '')
    .substr(0, 6);
  return id;
}

export function createApplicationName(business: IBusiness) {
  let businessInitials = 'AA';
  if (business.name) businessInitials = business?.name.substring(0, 2);
  let randomLetters = Math.random()
    .toString(36)
    .replace(/[^a-z]+/g, '')
    .substr(0, 2);
  const id = generateSixDigitId();
  const applicationName = businessInitials + randomLetters + id;
  return applicationName.toUpperCase();
}

async function mergePlans(
  lines: Array<string>,
  existingPlan: string,
  customerMessage: string = null,
  trackEvent: (...args) => any,
) {
  const clonedApp = await cloneApplication({
    id: existingPlan,
    message: customerMessage,
  });
  await createApplication({
    name: clonedApp?.name,
    existingPlan: clonedApp.id,
  });
  const res = await requestChangeActiveLine({
    applicationId: existingPlan,
    newApplicationId: clonedApp?.id,
    comment: customerMessage || null,
    changedLineIds: lines,
    isNewLines: true,
  });

  if (!res?.existingRequest) {
    trackEvent({
      event: 'application_submitted',
      application_id: existingPlan,
      application_type: ApplicationType.CHANGE_REQUEST,
      num_lines: lines?.length,
    });
  }

  return clonedApp;
}

export async function submitNewApplication(
  lines: Array<string>,
  business: IBusiness,
  customerMessage: string = null,
  existingPlan: string = null,
  trackEvent: (...args) => any,
) {
  let application = null;
  if (existingPlan) {
    const clonedApp = await mergePlans(
      lines,
      existingPlan,
      customerMessage,
      trackEvent,
    );
    application = {
      id: clonedApp?.id,
      name: clonedApp?.name,
      application_type: ApplicationType.CHANGE_REQUEST_NEW_LINES,
    };
  }
  if (!existingPlan) {
    const name = createApplicationName(business);
    application = await createApplication({
      name,
      customerMessage,
    });
    application['type'] = ApplicationType.NEW_PLAN;
  }
  await Promise.all(
    lines.map((lineId) => editLine(lineId, null, null, null, application?.id)),
  );

  if (application && application?.id) {
    // this functionality should not crash the process (this is only an automatic optimization)
    alignLineAndCalculatePaymentTable(application.id).catch((err) => undefined);
    if (!existingPlan) {
      // create a snapshot for a new application
      createPlanSnapshot(application.id).catch((err) => undefined);
    }
  }

  trackEvent({
    event: 'application_submitted',
    application_id: application?.id,
    application_type: application?.type,
    name: application?.name,
    num_lines: lines?.length,
  });
  return application?.name;
}

export function setLinesInState(
  linesInApplication: Array<string>,
  lines: Array<ILine>,
  setLines: (...args) => any,
) {
  if (lines) {
    const updatedLines = lines.map((line) => {
      if (linesInApplication.includes(line.id)) {
        line['status'] = applicationStatus.submitted.status;
      }
      return line;
    });
    setLines(updatedLines);
  }
}

export function verifyPreActivationStepData(
  step: IPreActivationStep,
  marked: boolean,
) {
  if (!step || !step.last_modified_at) {
    return false;
  }
  return step.marked === marked;
}
